import {gql} from '@apollo/client';

import {SequenceTopology} from 'constants/SequenceConstants';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  getBasesValueFromBioentitySummary,
  getExistingFeaturesStringFromBioentitySummary,
} from 'notebook/structured-tables/bases-columns/BasesColumnConstants';
import {isWithSchemaFieldColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  RegistrationTableSchemaTagsFromExistingBioentityCustomEvalQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {
  convertTagToTypedValue,
  formatTypedValue,
  isTypedValueEmpty,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {findSchemaTagByApiId} from 'schemas/tags/TagUtil';

import {BioentityQuery, BioentityQueryVariables} from './__generated/BioentityQuery';

type BioentityQueryMoleculeNode = ExtractByTypename<BioentityQuery['directBioentity'], 'MoleculeNode'>;

type BioentityQuerySequenceNode = ExtractByTypename<BioentityQuery['directBioentity'], 'SequenceNode'>;

const BIOENTITY_QUERY = gql`
  query BioentityQuery($input: RegistryInput!) @skipAudit @engComponent(name: REGISTRY) {
    directBioentity(input: $input) {
      ...BioentitySummaryOrInaccessibleGraphQL
      ... on SequenceNode {
        id
        name
        tagsJson
        basesIfShowResidues
        bases
        circular
        modifiedNucleotides {
          position
          sugarMonomerId
          baseMonomerId
          phosphateMonomerId
        }
        helmString
        idtString
        customSyntaxString
        existingFeatures
      }
      ... on MoleculeNode {
        id
        canonicalSmiles
      }
      ... on ProteinNode {
        id
        versionJson
      }
    }
  }
  ${getFragmentWrapper('BioentitySummaryOrInaccessible').fragment}
`;

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doQuery(bioentityId: string, customSyntaxName?: string | null): Promise<BioentityQuery> {
    return await doQueryWithoutCache<BioentityQuery, BioentityQueryVariables>(
      {query: BIOENTITY_QUERY},
      {variables: {input: {id: bioentityId, customSyntaxName}}}
    );
  },
};

export async function autopopulateFromBioentitySummary(
  query: RegistrationTableSchemaTagsFromExistingBioentityCustomEvalQuery
): Promise<ServerResolutionResult> {
  const {bioentityId, columnDefinition, typedValue} = query;
  const customSyntaxName =
    columnDefinition.type === TableColumnType.BASES
      ? columnDefinition.basesColumnConfig.customSyntaxName
      : undefined;
  const data = await stubbableFunctions.doQuery(bioentityId, customSyntaxName);
  const bioentitySummary = data.directBioentity;
  if (isInaccessible(bioentitySummary)) {
    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(''));
  }
  if (
    bioentitySummary != null &&
    Object.keys(bioentitySummary).includes('tagsJson') &&
    isWithSchemaFieldColumnDefinition(columnDefinition)
  ) {
    const schemaTag = findSchemaTagByApiId(bioentitySummary.tagsJson, columnDefinition.tagSchemaFieldApiId);
    return makeCustomResolutionResultFromTypedValue(
      schemaTag ? convertTagToTypedValue(schemaTag) : typedValue
    );
  } else if (
    bioentitySummary != null &&
    Object.keys(bioentitySummary).includes('name') &&
    columnDefinition.type === TableColumnType.BIOENTITY_NAME
  ) {
    return makeCustomResolutionResultFromTypedValue(toStringTypedValue(bioentitySummary.name));
  } else if (
    bioentitySummary != null &&
    (columnDefinition.type === TableColumnType.DNA_BASES ||
      columnDefinition.type === TableColumnType.RNA_BASES)
  ) {
    if (Object.keys(bioentitySummary).includes('basesIfShowResidues')) {
      return makeCustomResolutionResultFromTypedValue(
        toStringTypedValue((bioentitySummary as BioentityQuerySequenceNode).basesIfShowResidues || '')
      );
    }
  } else if (
    bioentitySummary != null &&
    (columnDefinition.type === TableColumnType.DNA_IDT || columnDefinition.type === TableColumnType.RNA_IDT)
  ) {
    if (Object.keys(bioentitySummary).includes('idtString')) {
      return makeCustomResolutionResultFromTypedValue(
        toStringTypedValue((bioentitySummary as BioentityQuerySequenceNode).idtString || '')
      );
    }
  } else if (
    bioentitySummary != null &&
    (columnDefinition.type === TableColumnType.DNA_HELM || columnDefinition.type === TableColumnType.RNA_HELM)
  ) {
    if (Object.keys(bioentitySummary).includes('helmString')) {
      return makeCustomResolutionResultFromTypedValue(
        toStringTypedValue((bioentitySummary as BioentityQuerySequenceNode).helmString || '')
      );
    }
  } else if (bioentitySummary != null && columnDefinition.type === TableColumnType.SMILES) {
    if (Object.keys(bioentitySummary).includes('canonicalSmiles')) {
      return makeCustomResolutionResultFromTypedValue(
        toStringTypedValue((bioentitySummary as BioentityQueryMoleculeNode).canonicalSmiles || '')
      );
    }
    // TODO BNCH-52395: Unify oligo and BASES table column types.
  } else if (bioentitySummary != null && columnDefinition.type === TableColumnType.BASES) {
    const resolvedBases =
      getBasesValueFromBioentitySummary(columnDefinition.basesColumnConfig, bioentitySummary) || '';
    const basesTypedValue = toStringTypedValue(resolvedBases);
    const existingFeaturesString = getExistingFeaturesStringFromBioentitySummary(
      columnDefinition.basesColumnConfig,
      bioentitySummary
    );
    if (
      existingFeaturesString != null &&
      (resolvedBases !== formatTypedValue(typedValue) ||
        columnDefinition.basesColumnConfig.monomerMappingConfig != null) &&
      // We need to check for empty both here and in basesCustomEval because any error from this function prevents the resolved value from being passed up.
      !isTypedValueEmpty(typedValue)
    ) {
      return makeCustomResolutionResultFromTypedValue(basesTypedValue, existingFeaturesString, true);
    } else {
      return makeCustomResolutionResultFromTypedValue(basesTypedValue);
    }
  } else if (bioentitySummary != null && columnDefinition.type === TableColumnType.SEQUENCE_TOPOLOGY) {
    if (bioentitySummary.__typename === 'SequenceNode') {
      return makeCustomResolutionResultFromTypedValue(
        toStringTypedValue(bioentitySummary.circular ? SequenceTopology.CIRCULAR : SequenceTopology.LINEAR)
      );
    }
  }

  return makeCustomResolutionResultFromTypedValue(toStringTypedValue(''));
}
